import { useContext } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  type Params,
} from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  usePortalReviewScenarios,
  usePortalSignInAdapter,
} from "@shared-core/data/PortalSignInAdapterContext";
import {
  FileText,
  Package,
  PoundSterling,
  ScanSearch,
  ChevronDown,
  ShoppingBag,
  ShoppingBasket,
} from "lucide-react";
import { cn } from "../../app/components/primitives/utils";
import { Button } from "../../app/components/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../app/components/primitives/dropdown-menu";
import { PortalAccountMenuContext } from "./PortalAccountMenuContext";
import { PortalViewerProvider, usePortalViewer } from "./PortalViewerContext";
import { PortalBasketReviewContext } from "./PortalReviewContext";
import { PortalFooter } from "./PortalFooter";
import {
  CustomerBasketProvider,
  useCustomerBasket,
} from "./basket/CustomerBasketProvider";
import { usePortalSignInGate } from "./signin/usePortalSignInGate";
import {
  PortalSignInFailed,
  PortalSignInLoading,
} from "./signin/PortalSignInProgress";
import { usePortalTheme } from "./theme/PortalThemeContext";
import { PortalThemeScope } from "./theme/PortalThemeScope";
import { ThemedSurface } from "./theme/ThemedSurface";
import { PopUpNotice, TopBarNotices } from "./theme/PortalNotices";

// Portal chrome. Nav items — Orders, Quotes, Catalogue, Basket, Check artwork,
// Spend — per the agreed IA (option B) for Orders/Spend: current orders is the
// default filter on ONE orders list rather than its own nav item, so search
// reaches everything and a buyer does not have to classify a thing before they
// can find it. Proofs live on each order's page (the standalone Proofs item
// went with prepress_proofing_module, #2554). Check artwork is its own item:
// "is my file OK to print" is a standalone question a customer can act on
// before a job or order even exists for it.
//
// Spend is hidden entirely for a viewer who cannot see it, Catalogue for
// one who cannot order, and Quotes for one with no quote to see; a nav item that 403s
// (or opens empty) would be worse than one that is absent. All are the session's own facts
// (`can_see_spend`, `can_order_by_card`, `has_quotes`):
// the portal derives no access state. While the session is still loading,
// Spend and Catalogue stay hidden rather than flashing in and then
// disappearing. Check artwork has no such gate: the journey is genuinely
// available to any signed-in contact.

function PortalNav() {
  const { sessionState } = usePortalViewer();
  const location = useLocation();

  // Spend needs the customer's spend switch on AND the person holding the extra:
  // the server worked that out into one fact.
  const hasSpendReporting =
    sessionState.status === "success" && sessionState.data.can_see_spend;
  // Ordering (docs/features/customer-portal-ordering §9d): same "hidden
  // entirely rather than a 403" convention as Spend above. Card-only/
  // credit-stop accounts still see this item (card ordering is basic) —
  // PortalCataloguePage itself explains the account state when they get there.
  const hasOrdering =
    sessionState.status === "success" && sessionState.data.can_order_by_card;
  // Quotes (P4f, doc 30 Question 23): shown only when the person can see at least one CIS quote, so a customer
  // with none never meets an empty tab. The session's own fact, from one existence check.
  const hasQuotes =
    sessionState.status === "success" && sessionState.data.has_quotes;

  // Basket (portal ordering spec 17 section 3.5): present only where the build has a basket and the person
  // can order; the count is the lines in it, from the same read the basket page uses. A build that cannot
  // change a basket shows the entry only when there is something in it to see.
  const basket = useCustomerBasket();
  const basketCount =
    basket?.state.status === "success" ? basket.state.data.lines.length : 0;
  const hasBasketToShow =
    basket !== null && (basket.capabilities.changes || basketCount > 0);

  const items = [
    { to: paths.portalOrders(), label: "Orders", icon: Package, show: true },
    {
      to: paths.portalQuotes(),
      label: "Quotes",
      icon: FileText,
      show: hasQuotes,
    },
    {
      to: paths.portalCatalogue(),
      label: "Catalogue",
      icon: ShoppingBag,
      show: hasOrdering,
    },
    {
      to: paths.portalBasket(),
      label: "Basket",
      icon: ShoppingBasket,
      show: hasBasketToShow && hasOrdering,
      count: basketCount > 0 ? basketCount : undefined,
      countClassName: "bg-primary/10 text-primary",
    },
    {
      to: paths.portalArtworkCheck(),
      label: "Check artwork",
      icon: ScanSearch,
      show: true,
    },
    {
      to: paths.portalSpend(),
      label: "Spend",
      icon: PoundSterling,
      show: hasSpendReporting,
    },
  ].filter((i) => i.show);

  return (
    <nav
      // Never scrolls sideways, and never widens the page (a wider page pushes bottom-fixed elements
      // off-screen): items that do not fit on one line wrap onto the next.
      className="flex flex-wrap gap-1"
      aria-label="Portal sections"
    >
      {items.map(({ to, label, icon: Icon, count, countClassName }) => {
        const active = location.pathname.startsWith(to.split("?")[0]);
        return (
          <NavLink
            key={label}
            to={to}
            className={cn(
              "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            <Icon className="size-4" />
            {label}
            {count !== undefined && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-xs font-semibold",
                  countClassName ??
                    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
                )}
              >
                {count}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

/** What the landing gate learned about the person, for the account menu. */
export interface PortalSignedIn {
  personName: string | null;
  /** The customer they are working as, when they chose one. */
  customerName: string | null;
  /** How many customers they are linked to, blocked or not. */
  linkedCount: number;
}

/**
 * The account menu: who is signed in, which customer they are working as, "Change customer" for a
 * person with several, and sign out. The mock build alone adds the review tool (a persona switcher
 * and a jump to the sign-in states): the real portal has neither, and no way to be someone else.
 */
export function ViewerMenu({
  personName,
  customerName,
  linkedCount,
}: PortalSignedIn) {
  const { review, sessionState } = usePortalViewer();
  const adapter = usePortalSignInAdapter();
  const reviewScenarios = usePortalReviewScenarios();
  const basketReview = useContext(PortalBasketReviewContext);
  const basket = useCustomerBasket();
  const navigate = useNavigate();

  // A chosen customer means a person has signed in as someone with that link, so the review personas
  // (stand-ins for people the mock cannot sign in as) no longer apply.
  const showPersonas = review !== null && customerName === null;
  const shownName =
    personName ?? (showPersonas ? review.persona.name : "Signed in");
  const shownCustomer =
    customerName ??
    (showPersonas
      ? review.persona.jobTitle
      : sessionState.status === "success"
        ? sessionState.data.customer_name
        : null);

  async function signOut() {
    try {
      await adapter.signOut();
    } finally {
      // The choice is forgotten whatever the server said; the sign-in page is where to try again.
      navigate(paths.portalSignIn());
    }
  }

  // A long name must never push the nav: the trigger has a ceiling and each name truncates inside it.
  // The full text stays in the DOM (the accessible name) and in `title` (the hover tooltip). Beside the
  // six-item nav in the default 976px content width (`lg`), 12rem is what is left; with the nav on its
  // own row there is more room.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto min-w-0 max-w-[min(100%,12rem)] sm:max-w-[16rem] lg:max-w-[12rem] shrink py-1.5 text-left"
          title={[shownName, shownCustomer].filter(Boolean).join(", ")}
        >
          <span className="flex min-w-0 flex-col items-end">
            <span className="max-w-full truncate text-sm font-medium leading-tight">
              {shownName}
            </span>
            {shownCustomer && (
              <span className="max-w-full truncate text-xs leading-tight text-muted-foreground">
                {shownCustomer}
              </span>
            )}
          </span>
          <ChevronDown className="size-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {linkedCount > 1 && (
          <DropdownMenuItem
            onSelect={() =>
              navigate(paths.portalChooseCustomer({ change: true }))
            }
          >
            Change customer
            <span className="ml-auto text-xs text-muted-foreground">
              {linkedCount} linked
            </span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => void signOut()}>
          Sign out
        </DropdownMenuItem>
        {reviewScenarios && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate(paths.portalWelcome())}>
              Review: sign-in states
            </DropdownMenuItem>
          </>
        )}
        {basketReview && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Review tool: put this person&apos;s basket into a state. Not part
              of the real portal.
            </DropdownMenuLabel>
            {basketReview.listBasketReviewOptions().map((option) => (
              <DropdownMenuItem
                key={option.id}
                onSelect={() => {
                  void basketReview
                    .applyBasketReviewOption(option.id)
                    .then(() => basket?.refresh());
                }}
              >
                {option.group === "basket" ? "Basket: " : ""}
                {option.label}
              </DropdownMenuItem>
            ))}
          </>
        )}
        {showPersonas && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Review tool — switch persona to see how the two scopes change each
              screen. Not part of the real portal.
            </DropdownMenuLabel>
            {review.personas.map((p) => (
              <DropdownMenuItem
                key={p.id}
                onSelect={() => review.setPersonaId(p.id)}
                className="flex flex-col items-start gap-0.5"
              >
                <span className="text-sm font-medium">
                  {p.name}
                  {p.id === review.persona.id ? " ·" : ""}
                </span>
                <span className="text-xs text-muted-foreground">
                  {p.jobTitle}
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A page opts into the wider content width (about 1200px, for pages that lay out
 * side by side, like the catalogue's product page) with `handle: { wide: true }`
 * on its route, or into `max-w-6xl` (the quote page, which has a decision panel beside its
 * content from `lg` up) with `handle: { width: "6xl" }`. Everything else keeps the default
 * `max-w-5xl`.
 */
export interface PortalRouteHandle {
  /** Always wide, or decided by the address's own parameters (one route serving two pages of different widths). */
  wide?: boolean | ((params: Params<string>) => boolean);
  /** A fixed width between the default and wide, for the one page that needs exactly that. */
  width?: "6xl";
}

function useContentWidth(): string {
  const matches = useMatches();
  const isWide = matches.some((match) => {
    const wide = (match.handle as PortalRouteHandle | undefined)?.wide;
    return typeof wide === "function" ? wide(match.params) : wide === true;
  });
  if (isWide) return "max-w-[1200px]";
  const is6xl = matches.some(
    (match) => (match.handle as PortalRouteHandle | undefined)?.width === "6xl",
  );
  return is6xl ? "max-w-6xl" : "max-w-5xl";
}

function PortalChrome({ signedIn }: { signedIn: PortalSignedIn }) {
  const maxWidth = useContentWidth();
  const renderAccountMenu = useContext(PortalAccountMenuContext);
  const { sessionState } = usePortalViewer();
  const themed = usePortalTheme();
  const values = themed?.theme?.values ?? null;
  const displayName = values?.display_name ?? "Pollards";

  return (
    <ThemedSurface theme={values} className="min-h-screen">
      {themed && (
        <>
          <TopBarNotices notices={themed.notices} className={maxWidth} />
          <PopUpNotice
            notices={themed.notices}
            onDismiss={themed.dismissNotice}
          />
        </>
      )}
      <header className="border-b bg-card">
        <div
          className={cn(
            "mx-auto flex items-center justify-between gap-4 px-4 py-3 sm:px-6",
            maxWidth,
          )}
        >
          <div className="flex min-w-0 items-center gap-6">
            <NavLink
              to={paths.portalHome()}
              className="flex shrink-0 items-center"
              aria-label={`${displayName} home`}
            >
              {values?.logo_image ? (
                <img
                  src={values.logo_image}
                  alt={displayName}
                  className="h-8 w-auto max-w-48 object-contain"
                />
              ) : (
                <span className="text-sm font-semibold tracking-tight">
                  {displayName}
                </span>
              )}
            </NavLink>
            <div className="hidden min-w-0 lg:block">
              <PortalNav />
            </div>
          </div>
          {renderAccountMenu ? (
            renderAccountMenu(signedIn)
          ) : (
            <ViewerMenu {...signedIn} />
          )}
        </div>
        {/* Below `lg` the nav moves to its own row so the account menu keeps its place — buyers check
            status on a phone (brief §11) — and the row wraps rather than scrolls. The header row fits
            the full six-item nav only from `lg`. */}
        <div className="border-t px-4 py-2 sm:px-6 lg:hidden">
          <PortalNav />
        </div>
      </header>
      <main className={cn("mx-auto px-4 py-6 sm:px-6", maxWidth)}>
        <Outlet />
      </main>
      <PortalFooter
        legalDetails={
          sessionState.status === "success"
            ? sessionState.data.legal_details
            : null
        }
        className={maxWidth}
      />
    </ThemedSurface>
  );
}

/** One basket read for the layout and its pages, started afresh for each person and customer. */
function BasketScope({ children }: { children: React.ReactNode }) {
  const { viewKey } = usePortalViewer();
  return (
    <CustomerBasketProvider key={viewKey}>{children}</CustomerBasketProvider>
  );
}

/**
 * The portal's layout and its landing gate. Nothing inside renders until the gate has asked the
 * server who the signed-in person is and decided: the sign-in page when the sign-in has lapsed, the
 * welcome, chooser, no-access or blocked page, or into the portal as one customer
 * (docs/features/customer-storefront/28_frontends_p4_update_spec.md section 5.2).
 */
export function PortalLayout() {
  const gate = usePortalSignInGate();
  if (gate.status === "loading") return <PortalSignInLoading />;
  if (gate.status === "error")
    return <PortalSignInFailed onRetry={gate.retry} />;
  if (gate.status === "redirect") return <Navigate to={gate.to} replace />;
  return (
    <PortalViewerProvider customerId={gate.customer?.customer_id ?? null}>
      <BasketScope>
        <PortalThemeScope brandsDocument loading={<PortalSignInLoading />}>
          <PortalChrome
            signedIn={{
              personName: gate.personName,
              customerName: gate.customer?.customer_name ?? null,
              linkedCount: gate.linked.length,
            }}
          />
        </PortalThemeScope>
      </BasketScope>
    </PortalViewerProvider>
  );
}
